import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Truck, Loader2, AlertTriangle } from 'lucide-react';
import DateRangePicker from '../DateRangePicker';
import { LogisticsCards } from './LogisticsCards';
import { BrazilMap } from './BrazilMap';
import { FormasSection } from './FormasSection';
import { FreeShippingSection } from './FreeShippingSection';
import { CheckoutStep } from './CheckoutStep';
import { InsightsAdmin, InsightsPortal } from './Insights';
import { Insufficient, SectionTitle, fmtDate, fieldMessage, FieldNotice } from './format';

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toRange = (f) => {
  if (f.mode === 'custom' && f.startDate && f.endDate) return { startDate: f.startDate, endDate: f.endDate };
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - (Number(f.preset) - 1));
  return { startDate: ymd(start), endDate: ymd(end) };
};

const Section = ({ title, sub, ok, message, testId, children }) => (
  <section data-testid={testId}>
    <SectionTitle sub={sub}>{title}</SectionTitle>
    {ok ? children : <Insufficient message={message} testId={`${testId}-insufficient`} />}
  </section>
);

// Shared logistics report. `api` resolves paths against the panel or the portal base.
export const LogisticsReport = ({ api, isPortal = false }) => {
  const [filter, setFilter] = useState({ mode: 'preset', preset: '30', startDate: null, endDate: null });
  const [report, setReport] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = toRange(filter);

  const load = useCallback(async () => {
    setLoading(true);
    setCheckout(null);
    const params = { start_date: range.startDate, end_date: range.endDate };
    try {
      const { data } = await api.get('/report', { params });
      setReport(data);
    } catch (e) { setReport({ available: false, reason: 'Não foi possível carregar o relatório agora.' }); }
    finally { setLoading(false); }
    api.get('/checkout', { params }).then((r) => setCheckout(r.data)).catch(() => setCheckout({ available: false, reason: 'Não foi possível carregar o funil de checkout agora.' }));
  }, [api, range.startDate, range.endDate]);
  useEffect(() => { load(); }, [load]);

  const saveLimit = async (v) => {
    const { data } = await api.saveLimit(v);
    setReport((r) => ({ ...r, frete_gratis_limite: data.frete_gratis_limite, frete_gratis_configurado: data.frete_gratis_configurado }));
  };
  const saveFactor = async (v) => {
    await api.put('/settings', { frete_fator_custo: v });
    setReport((r) => ({ ...r, frete_fator_custo: v }));
  };

  const r = report;
  const unidade = r?.prazo_unidade;
  const fs = r?.field_status || {};
  const ctx = { isPortal, labels: r?.field_labels, progress: r?.importing?.progress };
  const msgFor = (field) => fieldMessage(fs[field], field, ctx);
  const colNote = ['valor_frete', 'prazo'].filter((f) => fs[f] && fs[f] !== 'ok');
  return (
    <div className="space-y-8" data-testid="logistics-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3"><Truck className="h-7 w-7 text-indigo-600" /> Logística e frete</h1>
          <p className="text-sm text-muted-foreground mt-1">Pedidos pagos, pela data do pedido. Comparação com o período anterior de mesma duração.</p>
        </div>
        <DateRangePicker mode={filter.mode} preset={filter.preset} startDate={filter.startDate} endDate={filter.endDate} onChange={(f) => setFilter((p) => ({ ...p, ...f }))} disabled={loading} />
      </div>

      {loading && <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>}
      {!loading && r && !r.available && (
        <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="logistics-unavailable">{r.reason}</CardContent></Card>
      )}
      {!loading && r?.available && (
        <>
          {r.partial && (
            <Alert data-testid="logistics-partial-alert">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Dados parciais: as informações de logística começam em {fmtDate(r.history_start_date)}.</AlertDescription>
            </Alert>
          )}
          {r.importing?.active && (
            <Alert data-testid="logistics-importing-alert">
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>{fieldMessage('importing', null, ctx)}</AlertDescription>
            </Alert>
          )}
          <p className="text-xs text-muted-foreground -mt-4" data-testid="logistics-period">
            {fmtDate(r.start_date)} a {fmtDate(r.end_date)} · {r.pedidos} pedidos pagos · comparado com {fmtDate(r.previous.start_date)} a {fmtDate(r.previous.end_date)}
          </p>

          <Section title="Visão geral" testId="logistics-overview" ok
            sub={unidade === 'uteis' ? 'Prazo em dias úteis (data estimada de entrega não informada).' : 'Prazo em dias corridos entre o pedido e a entrega prometida.'}>
            <LogisticsCards cards={r.cards} unidade={unidade} ctx={ctx} />
          </Section>

          <Section title="Mapa por estado" testId="logistics-map-section" ok={!!r.by_uf} message={msgFor('uf')}>
            {r.by_uf && <BrazilMap rows={r.by_uf} unidade={unidade} />}
            {r.by_uf && colNote.map((f) => <FieldNotice key={f} compact status={fs[f]} field={f} ctx={ctx} testId={`map-col-notice-${f}`} />)}
          </Section>

          <Section title="Formas de entrega" testId="logistics-formas-section" ok={!!r.formas} message={msgFor('forma_entrega')}>
            {r.formas && <FormasSection rows={r.formas} unidade={unidade} />}
            {r.formas && colNote.map((f) => <FieldNotice key={f} compact status={fs[f]} field={f} ctx={ctx} testId={`formas-col-notice-${f}`} />)}
          </Section>

          <Section title="Frete grátis: distribuição e simulador" testId="logistics-free-shipping" ok message={msgFor('valor_frete')}>
            <FreeShippingSection report={r} editable={!isPortal} onSaveLimit={saveLimit} onSaveFactor={saveFactor} notice={msgFor('valor_frete')} auditApi={isPortal ? null : api} />
          </Section>

          {(!isPortal || checkout?.available) && (
            <section data-testid="logistics-checkout-section">
              <SectionTitle sub="Funil de checkout do GA4 no mesmo período.">Etapa de frete no checkout</SectionTitle>
              <CheckoutStep data={checkout} isPortal={isPortal} />
            </section>
          )}

          {isPortal ? <InsightsPortal api={api} /> : (
            <section>
              <SectionTitle sub="Geradas pela IA só com os números deste relatório. Edite antes de publicar.">Recomendações</SectionTitle>
              <InsightsAdmin api={api} range={range} />
            </section>
          )}
        </>
      )}
    </div>
  );
};
